// Word references for one language — the i18n string store (reference.translation): key → text.
// Flat, paginated, searchable; add / edit (value) / delete. The key is fixed on edit (delete +
// re-add to rename) so we never orphan a key.
import { html, signal, navigate } from '../shared/t4.js';
import { icon } from '../shared/icons.js';
import { api } from '../shared/api.js';
import { shell } from './shell.js';
import { pager, perPageSelect, DEFAULT_PER_PAGE } from '../shared/pager.js';
import { focusModal } from '../shared/modal.js';
import { rowDelete } from '../shared/confirm.js';
import { flashId, keepScroll, revealRow } from '../shared/listnav.js';

export function languageDetailScreen(code){
  const lang    = signal(null);
  const rows    = signal([]);
  const total   = signal(0);
  const page    = signal(1);
  const pages   = signal(1);
  const perPage = signal(DEFAULT_PER_PAGE);
  const loading = signal(true);
  let timer, lastQ = '';

  const formOpen = signal(false), isNew = signal(true), editId = signal(null);
  const fKey = signal(''), fValue = signal('');
  const formErr = signal(''), saving = signal(false), invalid = signal({});
  const confirmId = signal(null), deleting = signal(false), delErr = signal('');

  (async () => {
    try { const r = await api.get('/admin/languages?q=' + encodeURIComponent(code) + '&per_page=50');
          lang.value = (r.data || []).find(l => l.code === code) || { code }; }
    catch { lang.value = { code }; }
  })();

  async function load(locateId){
    loading.value = true;
    const params = new URLSearchParams({ per_page: perPage.value, page: String(page.value) });
    if(lastQ && lastQ.trim()) params.set('q', lastQ.trim());
    if(locateId) params.set('locate', String(locateId));
    try { const r = await api.get('/admin/languages/' + encodeURIComponent(code) + '/translations?' + params.toString());
          rows.value = r.data || []; total.value = r.total || 0; pages.value = r.total_pages || 1;
          if(locateId && r.locate_page) page.value = r.locate_page; }
    catch { rows.value = []; total.value = 0; pages.value = 1; }
    loading.value = false;
  }
  load();
  function onSearch(e){ lastQ = e.target.value; page.value = 1; clearTimeout(timer); timer = setTimeout(load, 220); }
  function onPer(e){ perPage.value = e.target.value; page.value = 1; load(); }
  function goPage(p){ page.value = p; load(); }

  function openCreate(){ isNew.value = true; editId.value = null; fKey.value = ''; fValue.value = ''; formErr.value = ''; invalid.value = {}; formOpen.value = true; focusModal(); }
  function openEdit(t){ isNew.value = false; editId.value = t.id; fKey.value = t.key || ''; fValue.value = t.value || ''; formErr.value = ''; invalid.value = {}; formOpen.value = true; focusModal(); }
  const clearInvalid = k => { if(invalid.value[k]) invalid.value = { ...invalid.value, [k]: false }; };
  function closeForm(){ formOpen.value = false; }
  async function save(e){
    e.preventDefault();
    const inv = {};
    if(!fKey.value.trim())   inv.key   = true;
    if(!fValue.value.trim()) inv.value = true;
    invalid.value = inv;
    if(Object.keys(inv).length){ formErr.value = 'Please fill in the highlighted fields.'; return; }
    saving.value = true; formErr.value = '';
    try {
      const creating = isNew.value;
      const res = await api.put('/admin/languages/' + encodeURIComponent(code) + '/translations',
                    { key: fKey.value.trim(), value: fValue.value, create_only: creating });
      const id = res && res.id;
      formOpen.value = false;
      await (creating ? revealRow(load, id) : keepScroll(() => load(), id));
    } catch(err){ formErr.value = (err.data && err.data.error) || err.message || 'Save failed'; }
    saving.value = false;
  }
  function armDelete(id){ delErr.value = ''; confirmId.value = id; }
  function cancelDelete(){ confirmId.value = null; delErr.value = ''; }
  async function doDelete(id){
    deleting.value = true; delErr.value = '';
    try { await api.del('/admin/translations/' + id); confirmId.value = null; await load(); }
    catch(err){ delErr.value = (err.data && err.data.error) || err.message || 'Delete failed'; }
    deleting.value = false;
  }

  const list = () => {
    if(loading.value) return html`<div class="muted-block">Loading…</div>`;
    if(!rows.value.length) return html`<div class="muted-block">No word references yet.<br>
      <span class="muted-sub">Add a key (e.g. cashbook.title) and the text this language should show.</span></div>`;
    const cId = confirmId.value, del = deleting.value, err = delErr.value;   // subscribe to delete state
    const hl = flashId.value;                                                // row to highlight
    const pg = () => pager({ page: page.value, totalPages: pages.value, total: total.value,
                             perPage: Number(perPage.value), onPage: goPage });
    return html`<div class="list-wrap">
      ${err ? html`<div class="alert alert-danger py-2 mb-2">${err}</div>` : null}
      ${pg()}
      <div class="row-list">${rows.value.map(t => html`
        <div class=${'row-card' + (hl === t.id ? ' row-hl' : '')} @click=${() => openEdit(t)}>
          <span class="trans-key">${t.key}</span>
          <span class="row-main">${t.value}</span>
          ${rowDelete({ armed: cId === t.id, busy: del, error: cId === t.id ? err : '',
                        onArm: () => armDelete(t.id), onCancel: cancelDelete, onDelete: () => doDelete(t.id) })}
          <span class="row-chevron">✎</span>
        </div>`)}</div>
      ${pg()}
    </div>`;
  };

  const content = html`
    <div class="page">
      <div class="page-head with-action">
        <div>
          <a class="back-link" @click=${() => navigate('/languages')}>${icon('chevron-left', {size: 14})} Languages</a>
          <h1 class="page-title">Words — ${() => lang.value ? (lang.value.name || lang.value.code) : code}</h1>
          <div class="page-sub">i18n word references for <b>${code}</b> — key → displayed text</div>
        </div>
        <button class="btn btn-primary" @click=${openCreate}>+ Add word</button>
      </div>
      <div class="toolbar">
        <input class="form-control" type="search" placeholder="Search key or text…" @input=${onSearch}>
        ${perPageSelect(perPage.value, onPer)}
      </div>
      <div class="count-line">${total} word references</div>
      ${list}

      <div class="modal-overlay" ?hidden=${() => !formOpen.value}>
        <form class="modal-card" @submit=${save}>
          <div class="modal-head">
            <h3>${() => isNew.value ? 'Add word' : 'Edit word'}</h3>
            <button type="button" class="modal-x" @click=${closeForm}>✕</button>
          </div>
          <label class="form-label">Key</label>
          <input class=${() => 'form-control mb-2' + (invalid.value.key ? ' is-invalid' : '')} .value=${fKey} @input=${e => { fKey.value = e.target.value; clearInvalid('key'); }}
                 placeholder="e.g. cashbook.title" ?readonly=${() => !isNew.value}>
          <div class="form-note">${() => isNew.value
            ? 'Dotted i18n key — match the key the app renders.'
            : "Key can't change — delete & re-add to rename."}</div>
          <label class="form-label">Text</label>
          <textarea class=${() => 'form-control mb-2' + (invalid.value.value ? ' is-invalid' : '')} rows="2" .value=${fValue} @input=${e => { fValue.value = e.target.value; clearInvalid('value'); }}
                    placeholder="The text shown for this key in this language"></textarea>
          ${() => formErr.value ? html`<div class="alert alert-danger py-2 mb-2">${formErr}</div>` : null}
          <div class="form-actions">
            <span class="form-actions-spacer"></span>
            <button type="button" class="btn btn-outline" @click=${closeForm}>Cancel</button>
            <button type="submit" class="btn btn-primary" ?disabled=${() => saving.value}>${() => saving.value ? 'Saving…' : 'Save'}</button>
          </div>
        </form>
      </div>
    </div>`;

  return shell('languages', 'Languages', content);
}
